import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api, qs } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

/**
 * Server-side paginated order list. GET /api/orders returns
 * { orders, total_count, total_pages } and does the filtering in SQL, so
 * the client never holds more than one page — the monolith's habit of
 * pulling `limit=100` and slicing in JS does not survive a fleet with a
 * year of history.
 *
 * keepPreviousData is what makes paging feel instant: the current page
 * stays on screen (dimmed by the caller via isPlaceholderData) while the
 * next one loads, instead of the table collapsing to a spinner and back.
 */
export function useOrders(filters) {
  const tenantId = useTenantId();
  const { search, status, driverId, dateFrom, dateTo, page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: qk.orders(tenantId, { search, status, driverId, dateFrom, dateTo, page, pageSize }),
    queryFn: () =>
      api(
        `/api/orders${qs({
          tenant_id: tenantId,
          search,
          status,
          driver_id: driverId,
          date_from: dateFrom,
          date_to: dateTo,
          limit: pageSize,
          page,
        })}`,
      ),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/** Every in-flight order, for the map and the active-fleet bar. */
export function useActiveOrders() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.activeOrders(tenantId),
    queryFn: () => api(`/api/orders${qs({ tenant_id: tenantId, status: 'active', limit: 100 })}`),
    enabled: Boolean(tenantId),
    select: (data) => data?.orders ?? [],
    placeholderData: { orders: [] },
  });
}

/**
 * Realaus laiko ETA dispečerio sąraše — when each moving truck reaches its
 * NEXT stop, keyed by order id.
 *
 * A separate query from useActiveOrders() beside it, not a widened one, for
 * the reason the endpoint itself is separate (see live_eta() in
 * blueprints/orders.py): this is the only thing in the console that makes
 * outbound routing calls on a poll, and hanging it off the order list would
 * put that cost on the map, the orders table and every screen that reads an
 * order.
 *
 * Polled rather than invalidated. Every other cache entry here goes stale
 * when somebody CHANGES something, which is what useOrderInvalidation is
 * for; an ETA goes stale because time passed and a truck moved, and no
 * mutation in this product marks that. 60 seconds is a compromise between
 * the two costs: shorter and a 40-truck fleet spends its whole routing
 * budget re-answering a question whose answer moved by a minute, longer and
 * the number on screen stops being the one a dispatcher would quote down the
 * phone. The server caches each leg for 60 s of its own
 * (ROUTE_CACHE_TTL_SECONDS), so this cadence lands roughly one outbound
 * call per truck per minute at worst, and fewer once the fleet is warm.
 */
export function useLiveEta() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.liveEta(tenantId),
    queryFn: () => api('/api/orders/live-eta'),
    enabled: Boolean(tenantId),
    refetchInterval: 60_000,
    // Matches the interval: a re-mount inside the same minute reuses the
    // answer rather than paying for the fleet's routing again.
    staleTime: 60_000,
    select: (data) => data?.etas ?? {},
    // An ETA is an enrichment, never the thing the screen is about. A
    // routing service that is down must leave the live list rendering
    // exactly as it does today, with no ETA column, not an error state
    // over the dispatcher's landing screen.
    placeholderData: {},
    retry: false,
  });
}

export function useOrder(orderId) {
  return useQuery({
    queryKey: qk.order(orderId),
    queryFn: () => api(`/api/orders/${orderId}`),
    enabled: Boolean(orderId),
  });
}

/**
 * Mutations share one invalidation policy: touch an order and every list
 * that could contain it is marked stale — the orders table (all filter and
 * page combinations, hence the prefix key), the active-order set behind the
 * map, and the dashboard counters. Cheaper than it looks, since React Query
 * only refetches the queries that currently have a mounted observer.
 */
function useOrderInvalidation() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['orders', tenantId] });
    queryClient.invalidateQueries({ queryKey: qk.dashboard(tenantId) });
  };
}

/**
 * One order's audit trail — who did what, when.
 *
 * P1-13: this endpoint has existed for a long time and nothing in the
 * console called it, so the trail was API-only. That is its own version of
 * the finding: "who changed that tonnage" could not be asked at all, let
 * alone answered. And until the same fix, the answer it would have given
 * was "an administrator" for every dispatcher in the office.
 *
 * Fetched only when a modal is actually open — an order's history does not
 * change while somebody reads it, and prefetching one per table row would
 * be a request per row for a panel most of them never open.
 */
export function useOrderEvents(orderId) {
  return useQuery({
    queryKey: qk.orderEvents(orderId),
    queryFn: () => api(`/api/orders/${orderId}/events`),
    enabled: Boolean(orderId),
    staleTime: 30_000,
  });
}


/**
 * V-63. The stops this load actually made — GET /api/orders/<id>/dwell-records.
 *
 * The route has been admin-scoped and tested since the dwell engine shipped
 * and nothing in the console ever called it, so a load's own waiting could
 * only be read as a fleet-wide monthly aggregate. A dispatcher on the phone
 * with a client disputing a EUR 166 dwell line had nowhere to look up which
 * stop it came from.
 *
 * Same staleTime and same enabled-guard as useOrderEvents beside it: this is
 * opened per load from the inspector, not prefetched per table row.
 */
export function useOrderDwellRecords(orderId) {
  return useQuery({
    queryKey: qk.orderDwellRecords(orderId),
    queryFn: () => api(`/api/orders/${orderId}/dwell-records`),
    enabled: Boolean(orderId),
    staleTime: 30_000,
  });
}

/**
 * V-72. GET /api/orders/<id>/route-history — where the truck actually was,
 * fix by fix.
 *
 * Admin-scoped and tested since the dwell engine shipped, and nothing in
 * frontend/src ever called it. Its own docstring says what it is for: "so
 * the admin UI can draw the actual path a truck drove, not just its current
 * position (drivers.last_known_lat/lng, which only ever holds the latest
 * fix)". The admin UI never drew it.
 *
 * Measured on a live server: one delivery held 692 recorded fixes, of which
 * 380 fell inside the 95-minute stop billed at 48,75 EUR — and a dispatcher
 * on the telephone with the client disputing that line could not see one of
 * them.
 *
 * An EMPTY list is a real and common answer, not an error: the manual status
 * buttons work with no GPS signal at all, so a delivery can be complete and
 * correct with nothing here. The caller has to say which it is — an empty
 * panel means both "nothing was recorded" and "nobody looked".
 *
 * Same staleTime and enabled-guard as the two hooks above: opened per load
 * from the inspector, never prefetched per table row. A three-and-a-half
 * hour delivery is ~700 rows, which is why this is not on the orders table.
 */
export function useOrderRouteHistory(orderId) {
  return useQuery({
    queryKey: qk.orderRouteHistory(orderId),
    queryFn: () => api(`/api/orders/${orderId}/route-history`),
    enabled: Boolean(orderId),
    staleTime: 30_000,
  });
}

/**
 * V-298. GET /api/orders/<id>/truck-route — can the truck actually
 * assigned to this order legally reach the site at all.
 *
 * `state` carries the answer, not the HTTP status: "vehicle_unknown" (no
 * truck assigned, or no capacity_tons entered for it — nothing to ask
 * Valhalla), "unavailable" (Valhalla unreachable right now — a fact about
 * the routing container, not about the road), "no_legal_route" (Valhalla
 * answered: no road connects these two points for this truck's weight —
 * THE warning this hook exists for), "ok" (route present).
 *
 * Same staleTime/enabled-guard as useOrderRouteHistory beside it: opened
 * per load from the order inspector, not prefetched per table row.
 */
export function useOrderTruckRoute(orderId) {
  return useQuery({
    queryKey: qk.orderTruckRoute(orderId),
    queryFn: () => api(`/api/orders/${orderId}/truck-route`),
    enabled: Boolean(orderId),
    staleTime: 30_000,
  });
}

/**
 * PATCH /api/dwell-records/<id>/reason — correcting why a truck waited.
 *
 * Distinct from POST /api/orders/<id>/dwell-reason, which the Driver App
 * uses: that one only ever touches an order's currently-OPEN stop, so it
 * cannot fix a stop that has already ended. This is the admin override, and
 * it works on a closed record — which is every record a dispatcher is ever
 * looking at afterwards.
 *
 * The order's own caches go with it because the stop's reason is written
 * into an order event; the analytics prefix goes because V-62's by_reason
 * table is computed from exactly this column, and a correction that left it
 * showing the old answer would send a carrier to renegotiate with the wrong
 * supplier.
 */
export function useSetDwellReason(orderId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dwellRecordId, reason }) =>
      api(`/api/dwell-records/${dwellRecordId}/reason`, { method: 'PATCH', body: { reason } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.orderDwellRecords(orderId) });
      queryClient.invalidateQueries({ queryKey: qk.orderEvents(orderId) });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

/**
 * V-66. GET /api/orders/<id>/price-estimate — what this load SHOULD have
 * cost at the quarry's own price list.
 *
 * The route has been admin-scoped and tested since the pricing work landed,
 * and nothing in frontend/src ever called it. Its whole purpose is the
 * purchase side: the quarry's waybill carries a unit price and a total that
 * OCR reads off the ticket, and this says what the carrier's own price list
 * makes of the same load. A disagreement between the two is a price rise
 * nobody told the office about — or a wrong material grade, or a stale
 * price list — and until now nothing in the product compared them.
 *
 * Measured on a live server: list 9.80 EUR/t, waybill 10.60 EUR/t, 26.4 t
 * weighed. 21.12 EUR on one load; 1 267.20 EUR across sixty loads a month.
 *
 * `matched: false` is a real answer and not an error — the load's
 * cargo_type matches no priced material at that quarry — so it is returned
 * rather than guessed at, and the screen has to say which of the two it is.
 */
export function useOrderPriceEstimate(orderId) {
  return useQuery({
    queryKey: qk.orderPriceEstimate(orderId),
    queryFn: () => api(`/api/orders/${orderId}/price-estimate`),
    enabled: Boolean(orderId),
    staleTime: 30_000,
  });
}

/**
 * V-69. The two order-scoped Telegram actions whose own docstrings name the
 * screen they belong to — "Admin action from the Užsakymai tab's
 * order-detail modal" — and which nothing in frontend/src called.
 *
 *   POST /api/orders/<id>/telegram-info  re-sends the whole load: quarry,
 *     site, product, weight, nav link. Its docstring names the cases: "a
 *     driver missed the original dispatch message, was reassigned after it
 *     went out, or just needs a reminder."
 *   POST /api/orders/<id>/telegram-note  sends the order's current note.
 *
 * A 201 here means the attempt was LOGGED, never that the driver got it.
 * The reply is the telegram_messages row, and its `status` is the answer:
 * 'sent', 'failed' (with error_detail) or 'skipped_unlinked' — the driver
 * has never linked his Telegram, so nothing was even attempted. That last
 * one is the commonest cause of "I never got it" and it has its own fix,
 * which is why the caller must render the status rather than the 201.
 */
export function useSendOrderTelegram(orderId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind) =>
      api(`/api/orders/${orderId}/telegram-${kind}`, { method: 'POST', body: {} }),
    // The Telegram tab keeps the log of every message; a re-send made from
    // the order modal has to appear there too, or the history a dispatcher
    // checks when a driver disputes what he was told is incomplete.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['telegram-messages'] }),
  });
}

/**
 * Transporto kainodaros modeliai: POST /api/orders/<id>/pricing-method.
 * Same refusal shape and same reason as useSetAgreedPrice's route — a
 * cancelled or already-invoiced load has nothing left to price — and the
 * same invalidation set as AgreedRateSection's inline mutation, since this
 * is the other half of the same "what does this load cost" question.
 */
export function useSetPricingMethod(orderId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      api(`/api/orders/${orderId}/pricing-method`, { method: 'POST', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.order(orderId) });
      queryClient.invalidateQueries({ queryKey: qk.orderPriceEstimate(orderId) });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-draft'] });
    },
  });
}

/**
 * Transporto kainodaros modeliai: POST /api/dwell-records/<id>/waive —
 * geranoriškas atleidimas nuo prastovos mokesčio. Same invalidation shape
 * as useSetDwellReason next to it, since both change something on a stop
 * this order's dwell list already renders.
 */
export function useWaiveDwellRecord(orderId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dwellRecordId, reason }) =>
      api(`/api/dwell-records/${dwellRecordId}/waive`, { method: 'POST', body: { reason } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.orderDwellRecords(orderId) });
      queryClient.invalidateQueries({ queryKey: qk.orderEvents(orderId) });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useCreateOrder() {
  const tenantId = useTenantId();
  const invalidate = useOrderInvalidation();
  return useMutation({
    mutationFn: (payload) => api('/api/orders', { method: 'POST', body: { tenant_id: tenantId, ...payload } }),
    onSuccess: invalidate,
  });
}

export function useSetOrderStatus() {
  const invalidate = useOrderInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, status }) =>
      api(`/api/orders/${orderId}/status`, { method: 'POST', body: { status } }),
    onSuccess: (_data, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: qk.order(orderId) });
      invalidate();
    },
  });
}

/** Reassigning a driver from the table's inline <select>. */
export function useUpdateOrder() {
  const invalidate = useOrderInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, ...patch }) => api(`/api/orders/${orderId}`, { method: 'PUT', body: patch }),
    onSuccess: (_data, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: qk.order(orderId) });
      invalidate();
    },
  });
}

/**
 * Bulk cancel. Sequential rather than Promise.all: these are writes against
 * a single-writer SQLite database, and firing twenty concurrent POSTs at it
 * trades a barely-noticeable speedup for lock contention. Partial failure is
 * reported rather than swallowed — the caller shows which ids did not go
 * through instead of a blanket "something failed".
 */
/**
 * Masinis kelių užsakymų perkėlimas / atšaukimas vienu veiksmu.
 *
 * ONE request, not a loop. This used to fire one POST .../status per ticked
 * row and count the failures afterwards, which meant a selection of fourteen
 * could come back six cancelled, one refused and seven still rolling — the
 * dispatcher made one decision and the fleet did something else, with the
 * only record of the difference in a toast.
 *
 * POST /api/orders/bulk checks the whole batch before touching any of it and
 * refuses the lot if one row cannot take the action, naming which and why
 * (see bulk_order_action() in blueprints/orders.py). The refusal arrives as
 * an ApiError whose body carries `problems`, so the caller can list the
 * offending rows rather than showing "409".
 *
 * `body` is passed through as-is: {order_ids, action, reason, and for a move
 * exactly one of quarry_id/site_id}.
 */
/**
 * Rytas planuojamas kartu — POST /api/orders/batch-assign.
 *
 * Preview by default (`apply` omitted), which is the endpoint's own default
 * and the reason this is a mutation the dispatcher runs twice: once to see
 * the proposal and what it saves against the way the product assigns today,
 * once to commit it. A planner that only ever applied would be asking for
 * trust it has not earned yet.
 *
 * Invalidates on apply only — a preview writes nothing, and refetching the
 * order list after it would just make the screen flicker for no change.
 */
export function useBatchAssign() {
  const invalidate = useOrderInvalidation();
  return useMutation({
    mutationFn: (body = {}) => api('/api/orders/batch-assign', { method: 'POST', body }),
    onSuccess: (result) => {
      if (result?.applied) invalidate();
    },
  });
}

export function useBulkOrderAction() {
  const invalidate = useOrderInvalidation();
  return useMutation({
    mutationFn: (body) => api('/api/orders/bulk', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}
